import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { CookieConsentContext } from "@/contexts/CookieConsentContext";
import { Link } from "react-router-dom";

const Toggle = ({ checked, onChange, disabled, label, hint }) => (
  <label className={`flex items-center justify-between py-2 ${disabled ? 'opacity-60' : ''}`}>
    <div>
      <div className="font-medium">{label}</div>
      {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
    </div>
    <input
      type="checkbox"
      className="h-5 w-5"
      checked={checked}
      onChange={(e) => onChange?.(e.target.checked)}
      disabled={disabled}
    />
  </label>
);

export default function CookieConsent() {
  const { t } = useTranslation();
  const cookie = useContext(CookieConsentContext);
  const [draft, setDraft] = useState(cookie.prefs);
  // Sync draft when prefs change (e.g., after acceptAll)
  useEffect(() => { setDraft(cookie.prefs); }, [cookie.prefs]);

  if (!cookie.visible) return null;

  const save = () => cookie.save(draft);

  return (
    <div className="fixed z-[1000] pointer-events-none">
      {/* Positioning container bottom-right mobile & desktop */}
      <div className="fixed bottom-3 right-3 w-[94vw] max-w-sm md:max-w-md pointer-events-auto">
        <div className="bg-background border rounded-lg shadow-xl">
          <div className="p-4">
            <div className="font-semibold text-base">Cookies & Daten</div>
            <div className="text-sm text-muted-foreground mt-1">
              Wir verwenden notwendige Cookies, um diese Website zu betreiben. Optional können Sie der Nutzung für Analyse/Marketing zustimmen. Details in unserer <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link>.
            </div>

            {/* Compact controls (banner) */}
            {!cookie.details ? (
              <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                <button
                  className="px-3 py-2 text-sm rounded border"
                  onClick={() => cookie.rejectNonEssential()}
                >Nur notwendige</button>
                <button
                  className="px-3 py-2 text-sm rounded border"
                  onClick={() => { setDraft(cookie.prefs); cookie.openPreferences(true); }}
                >Anpassen</button>
                <button
                  className="px-3 py-2 text-sm rounded bg-blue-600 text-white"
                  onClick={() => cookie.acceptAll()}
                >Alle akzeptieren</button>
              </div>
            ) : null}

            {/* Detailed settings (modal content) */}
            {cookie.details ? (
              <div className="mt-4 border-t pt-3">
                <div className="space-y-1">
                  <Toggle
                    checked={true}
                    disabled
                    label="Notwendig"
                    hint="Erforderlich für Grundfunktionen (z. B. Login, Sicherheit)."
                  />
                  <Toggle
                    checked={!!draft.analytics}
                    onChange={(v) => setDraft({ ...draft, analytics: v })}
                    label="Analyse"
                    hint="Anonyme Nutzungsstatistiken zur Verbesserung."
                  />
                  <Toggle
                    checked={!!draft.marketing}
                    onChange={(v) => setDraft({ ...draft, marketing: v })}
                    label="Marketing"
                    hint="Optionale Marketing-Integrationen (z. B. Pixel)."
                  />
                </div>
                <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                  <button className="px-3 py-2 text-sm rounded border" onClick={() => cookie.closePreferences()}>{t('common.close')}</button>
                  <button className="px-3 py-2 text-sm rounded border" onClick={() => cookie.rejectNonEssential()}>Nur notwendige</button>
                  <button className="px-3 py-2 text-sm rounded bg-blue-600 text-white" onClick={save}>Auswahl speichern</button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
